import React from 'react';
import { 
  FileText, 
  Building2, 
  Wrench, 
  Receipt, 
  Search, 
  Plus,
  AlertCircle,
  Inbox,
  KeyRound,
  Gauge
} from 'lucide-react';
import Button from './Button';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const iconMap = {
  invoices: Receipt,
  suppliers: Building2,
  services: Wrench,
  readings: Gauge,
  documents: FileText,
  credentials: KeyRound,
  search: Search,
  error: AlertCircle,
  default: Inbox,
};

const EmptyState = ({
  icon = 'default',
  title = 'No data found',
  description = 'Get started by creating your first item.',
  actionLabel,
  onAction,
  className = '',
}) => {
  const IconComponent = iconMap[icon] || iconMap.default;

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-6 text-center ${className}`}>
      <IconComponent className="w-10 h-10 text-text-muted mb-4" strokeWidth={1.5} />
      <p className="text-sm text-text-secondary mb-4 max-w-sm">
        {title}. {description}
      </p>
      {actionLabel && onAction && (
        <Button onClick={onAction} variant="primary" size="small">
          <Plus className="w-3.5 h-3.5" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export const EmptySuppliers = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="suppliers"
      title={t(KEYS.EMPTY_SUPPLIERS_TITLE)}
      description={t(KEYS.EMPTY_SUPPLIERS_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_SUPPLIERS_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptyServices = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="services"
      title={t(KEYS.EMPTY_SERVICES_TITLE)}
      description={t(KEYS.EMPTY_SERVICES_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_SERVICES_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptyInvoices = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="invoices"
      title={t(KEYS.EMPTY_INVOICES_TITLE)}
      description={t(KEYS.EMPTY_INVOICES_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_INVOICES_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptyReadings = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="readings"
      title={t(KEYS.EMPTY_READINGS_TITLE)}
      description={t(KEYS.EMPTY_READINGS_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_READINGS_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptyCredentials = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="credentials"
      title={t(KEYS.EMPTY_CREDENTIALS_TITLE)}
      description={t(KEYS.EMPTY_CREDENTIALS_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_CREDENTIALS_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptyReminders = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="default"
      title={t(KEYS.EMPTY_REMINDERS_TITLE)}
      description={t(KEYS.EMPTY_REMINDERS_DESCRIPTION)}
      actionLabel={t(KEYS.EMPTY_REMINDERS_ACTION)}
      onAction={onAdd}
    />
  );
};

export const EmptySearchResults = ({ query }) => {
  const { t } = useLanguage();
  const description = query
    ? t(KEYS.EMPTY_SEARCH_DESCRIPTION).replace('{query}', query)
    : t(KEYS.EMPTY_SEARCH_DESCRIPTION_GENERIC);
  return (
    <EmptyState
      icon="search"
      title={t(KEYS.EMPTY_SEARCH_TITLE)}
      description={description}
    />
  );
};

export const ErrorState = ({ message, onRetry }) => {
  const { t } = useLanguage();
  return (
    <EmptyState
      icon="error"
      title={t(KEYS.EMPTY_ERROR_TITLE)}
      description={message || t(KEYS.EMPTY_ERROR_DESCRIPTION)}
      actionLabel={onRetry ? t(KEYS.COMMON_RETRY) : undefined}
      onAction={onRetry}
    />
  );
};


export default EmptyState;
